/**
 * Bundle / quantity offer. Default mode passes the selected quantity to Horizon's product form
 * (its quantity selector, or a hidden `quantity` input when the selector is hidden), so the
 * shopper adds with Horizon's own button. Own-button mode adds via tk-cart-api.
 *
 * Per-item picker (data-pick): for bundles of 2+ the shopper chooses a value of one option per
 * item ("Item 1: Sky Bunny, Item 2: Honey Bear"). When the chosen items are not all the form's
 * variant, the add to cart (form button, sticky bar, own button) is taken over and the lines
 * are added through tk-cart-api (grouped per variant). Prices of the selected option are
 * recalculated from the chosen variants; when they all cost the same as the main variant the
 * Liquid-rendered prices are kept untouched.
 *
 * Shared modules are imported from the versioned asset URLs in data-tk-sync-url /
 * data-tk-cart-url (see tk-bundle.liquid), never from relative paths.
 */

/** @typedef {{id: number, o: string[], p: number, c: number, a: boolean}} TkVariant */

const TOKENS = ['quantity', 'percent', 'saving', 'total', 'unit_price', 'compare'];

/**
 * Builds a formatter that mimics the store's Liquid `money` output from a sample of 123456 cents.
 * @param {string | undefined} sample
 */
function moneyFormatter(sample) {
  const text = (sample ?? '').trim();
  const match = text.match(/\d[\d.,'\s  ]*\d|\d/);
  if (!match) return (/** @type {number} */ cents) => (cents / 100).toFixed(2);
  const run = match[0];
  const decimals = /[.,]56$/.test(run) ? 2 : 0;
  const decimalSep = decimals ? run.charAt(run.length - 3) : '';
  const intPart = decimals ? run.slice(0, -3) : run;
  const thousandsSep = intPart.length > 1 && !/\d/.test(intPart.charAt(1)) ? intPart.charAt(1) : '';
  const before = text.slice(0, match.index);
  const after = text.slice((match.index ?? 0) + run.length);
  return (/** @type {number} */ cents) => {
    const value = decimals ? Math.round(cents) / 100 : Math.round(cents / 100);
    const [whole = '0', fraction = ''] = (decimals ? value.toFixed(2) : String(value)).split('.');
    const grouped = thousandsSep ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep) : whole;
    return `${before}${grouped}${decimals ? decimalSep + fraction : ''}${after}`;
  };
}

class TkBundle extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {any} */
  #cart;
  /** @type {TkVariant[]} */
  #variants = [];
  /** @type {Map<Element, string>} Liquid/synced markup of each tier part, restored when not mixed. */
  #snapshot = new Map();
  /** @type {Set<string>} Item rows the shopper changed (others follow the main variant). */
  #touched = new Set();
  #pickIndex = 0;
  /** @type {(cents: number) => string} */
  #money = (cents) => (cents / 100).toFixed(2);
  #busy = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    // Group the tier radios per bundle block (the server name is only section-scoped).
    for (const radio of this.tierInputs) radio.name = `tk-bundle-${this.id}`;
    if (!this.selectedInput) {
      const first = this.tierInputs[0];
      if (first) first.checked = true;
    }
    this.#markSelected();
    this.#setupPicker(signal);

    Promise.all([import(this.dataset.tkSyncUrl ?? ''), import(this.dataset.tkCartUrl ?? '')])
      .then(([sync, cart]) => {
        if (signal.aborted) return;
        this.#cart = cart;
        sync.onVariantChange(
          this,
          (/** @type {any} */ { html, variant }) => {
            sync.syncParts(this, html);
            if (variant?.id) this.dataset.variantId = String(variant.id);
            const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add]'));
            if (button) button.disabled = !variant || variant.available === false;
            this.#takeSnapshot();
            this.#followMainVariant();
            // Horizon may snap or re-render its quantity input for the new variant.
            requestAnimationFrame(() => this.#apply());
          },
          signal
        );
        // Apply the preselected tier right away (hidden quantity input when the Quantity block
        // is off), then again once Horizon's quantity selector has upgraded. The timeout covers
        // pages where that component is never defined.
        this.#apply();
        return Promise.race([
          customElements.whenDefined('quantity-selector-component'),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
      })
      .then(() => {
        if (!signal.aborted) this.#apply();
      })
      .catch((error) => console.warn('[tk-bundle]', error));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @returns {HTMLInputElement[]} */
  get tierInputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input.tk-bundle-tier__input')]);
  }

  get selectedInput() {
    return this.tierInputs.find((input) => input.checked) ?? null;
  }

  get quantity() {
    return Math.max(1, Number(this.selectedInput?.value) || 1);
  }

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target.matches('[data-item-input]')) {
      const row = target.closest('[data-item]');
      if (row instanceof HTMLElement && row.dataset.item) this.#touched.add(row.dataset.item);
      this.#refreshItems();
      return;
    }
    if (!target.classList.contains('tk-bundle-tier__input')) return;
    this.#markSelected();
    this.#apply();
    this.#refreshItems();
  };

  #markSelected() {
    for (const tier of this.querySelectorAll('.tk-bundle-tier')) {
      tier.classList.toggle('is-selected', Boolean(tier.querySelector('input.tk-bundle-tier__input:checked')));
    }
  }

  /** Writes the selected quantity into Horizon's product form. */
  #apply() {
    if (this.hasAttribute('data-own-button') || !this.#cart) return;
    const productForm = this.#cart.findProductForm(this, this.dataset.productId);
    if (!productForm) return;
    const quantity = String(this.quantity);

    /** @type {any} */
    const selector = productForm.querySelector('quantity-selector-component');
    if (selector && typeof selector.setValue === 'function') {
      if (selector.getValue?.() === quantity) return;
      selector.setValue(quantity);
      selector.updateButtonStates?.();
      // Lets Horizon's sticky bar (and ours) show the new quantity.
      selector.onQuantityChange?.();
      return;
    }

    const form = productForm.querySelector('form');
    if (!form) return;
    let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.dataset.tkBundle = '';
      form.append(input);
    }
    input.value = quantity;
  }

  /* ---------------------------------------------------------------- per-item picker ---- */

  /** @param {AbortSignal} signal */
  #setupPicker(signal) {
    if (!this.hasAttribute('data-pick')) return;
    try {
      this.#variants = JSON.parse(this.querySelector('script[data-tk-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    if (this.#variants.length < 2) return;
    this.#pickIndex = Number(this.dataset.pickIndex) || 0;
    this.#money = moneyFormatter(this.dataset.moneySample);
    this.#takeSnapshot();
    this.#refreshItems();
    // Runs before Horizon's document-level (capture) submit handler, so mixed bundles are
    // added as several lines instead of N x the form's variant. Covers the form button and
    // sticky bars that click it.
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
  }

  /** @returns {TkVariant | undefined} */
  get mainVariant() {
    // data-variant-id is rendered by Liquid and updated on every variant change; the form's
    // id input is the fallback.
    const own = this.#variants.find((v) => v.id === Number(this.dataset.variantId));
    if (own) return own;
    const productForm = this.#cart?.findProductForm(this, this.dataset.productId);
    const formId = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="id"]'))?.value;
    return this.#variants.find((v) => v.id === Number(formId));
  }

  /**
   * Variant for one item: the main variant with the picked option changed to `value`.
   * Falls back to any available variant carrying that value.
   * @param {string} value
   * @returns {TkVariant | undefined}
   */
  #resolve(value) {
    const k = this.#pickIndex;
    const main = this.mainVariant;
    if (main) {
      const exact = this.#variants.find(
        (v) => v.o[k] === value && v.o.every((o, i) => i === k || o === main.o[i])
      );
      if (exact?.a) return exact;
      return this.#variants.find((v) => v.o[k] === value && v.a) ?? exact;
    }
    return this.#variants.find((v) => v.o[k] === value && v.a) ?? this.#variants.find((v) => v.o[k] === value);
  }

  /** @returns {HTMLElement[]} Rows shown for the selected quantity. */
  get activeRows() {
    const quantity = this.quantity;
    if (!this.hasAttribute('data-pick') || quantity < 2) return [];
    const rows = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-item]')]);
    return rows.length >= quantity ? rows.slice(0, quantity) : [];
  }

  /** @param {HTMLElement} row */
  #rowValue(row) {
    const select = row.querySelector('select[data-item-input]');
    if (select instanceof HTMLSelectElement) return select.value;
    const checked = row.querySelector('input[data-item-input]:checked');
    return checked instanceof HTMLInputElement ? checked.value : '';
  }

  /** @param {HTMLElement} row @param {string} value */
  #setRowValue(row, value) {
    const select = row.querySelector('select[data-item-input]');
    if (select instanceof HTMLSelectElement) {
      select.value = value;
      return;
    }
    for (const input of row.querySelectorAll('input[data-item-input]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    }
  }

  /** Untouched rows follow the main variant picker. */
  #followMainVariant() {
    const main = this.mainVariant;
    if (!main) return;
    const value = main.o[this.#pickIndex];
    for (const row of this.querySelectorAll('[data-item]')) {
      if (row instanceof HTMLElement && !this.#touched.has(row.dataset.item ?? '')) this.#setRowValue(row, value);
    }
    this.#refreshItems();
  }

  /** @returns {Array<{id: number, quantity: number}> | null} Lines for a mixed bundle, or null. */
  get pickedLines() {
    const rows = this.activeRows;
    if (rows.length === 0) return null;
    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const row of rows) {
      const variant = this.#resolve(this.#rowValue(row));
      if (!variant) return null;
      counts.set(variant.id, (counts.get(variant.id) ?? 0) + 1);
    }
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  /** Updates disabled states, the "Item n: value" labels and the selected tier's prices. */
  #refreshItems() {
    if (!this.hasAttribute('data-pick') || this.#variants.length < 2) return;
    for (const row of this.querySelectorAll('[data-item]')) {
      if (!(row instanceof HTMLElement)) continue;
      for (const input of row.querySelectorAll('[data-item-input], option')) {
        if (input instanceof HTMLInputElement || input instanceof HTMLOptionElement) {
          input.disabled = !this.#resolve(input.value)?.a;
        }
      }
      const value = this.#rowValue(row);
      const label = row.querySelector('[data-item-value]');
      if (label) label.textContent = value;
    }
    this.#refreshPrices();
  }

  #takeSnapshot() {
    this.#snapshot.clear();
    for (const part of this.querySelectorAll('.tk-bundle-tier__body, .tk-bundle-tier__prices')) {
      this.#snapshot.set(part, part.innerHTML);
    }
  }

  #restore() {
    for (const [part, html] of this.#snapshot) {
      if (part.isConnected && part.innerHTML !== html) part.innerHTML = html;
    }
  }

  #refreshPrices() {
    this.#restore();
    const rows = this.activeRows;
    const main = this.mainVariant;
    const tier = this.selectedInput?.closest('.tk-bundle-tier');
    if (!rows.length || !main || !(tier instanceof HTMLElement)) return;
    const variants = rows.map((row) => this.#resolve(this.#rowValue(row)));
    if (variants.some((v) => !v)) return;
    const picked = /** @type {TkVariant[]} */ (variants);
    // Same prices as the main variant: Liquid already rendered the exact numbers.
    if (picked.every((v) => v.p === main.p && v.c === main.c)) return;
    this.#renderTier(tier, picked);
  }

  /**
   * Same rules as tk-bundle-tier.liquid, using the chosen variants.
   * @param {HTMLElement} tier
   * @param {TkVariant[]} picked
   */
  #renderTier(tier, picked) {
    const d = tier.dataset;
    const qty = picked.length;
    const full = picked.reduce((sum, v) => sum + v.p, 0);
    const compareBase = picked.reduce((sum, v) => sum + Math.max(v.c || 0, v.p), 0);
    let percent = Math.min(100, Math.max(0, Number(d.percent) || 0));
    let final = full;
    if (percent > 0) final = Math.round((full * (100 - percent)) / 100);
    else if (Number(d.off) > 0) final = full - Number(d.off);
    final = Math.max(0, final);
    const saving = Math.max(0, full - final);
    if (percent === 0 && saving > 0 && full > 0) percent = Math.round((saving * 100) / full);
    const compare = final < full ? Math.max(full, compareBase) : compareBase;
    const unit = Math.round(final / qty);

    /** @type {Record<string, string>} */
    const values = {
      quantity: String(qty),
      percent: String(percent),
      saving: this.#money(saving),
      total: this.#money(final),
      unit_price: this.#money(unit),
      compare: this.#money(compare),
    };
    const fill = (/** @type {string | undefined} */ tpl) =>
      TOKENS.reduce((text, key) => text.split(`[${key}]`).join(values[key] ?? ''), tpl ?? '');
    const savingToken = (/** @type {string | undefined} */ tpl) => /\[(percent|saving)\]/.test(tpl ?? '');

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const el = tier.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('.tk-bundle-tier__title', fill(d.tplTitle));
    set('.tk-bundle-tier__subtitle', fill(d.tplSubtitle));
    const label = tier.querySelector('.tk-bundle-tier__label');
    if (label instanceof HTMLElement) {
      label.textContent = fill(d.tplLabel);
      label.hidden = saving <= 0 && savingToken(d.tplLabel);
    }
    const ribbon = tier.querySelector('.tk-bundle-tier__ribbon');
    if (ribbon instanceof HTMLElement) {
      ribbon.textContent = fill(d.tplRibbon);
      ribbon.hidden = saving <= 0 && savingToken(d.tplRibbon);
    }
    const perk = tier.querySelector('.tk-bundle-tier__perk');
    if (perk instanceof HTMLElement) perk.hidden = !(Number(d.freeMin) > 0 && final >= Number(d.freeMin));

    const prices = tier.querySelector('.tk-bundle-tier__prices');
    if (prices) {
      const parts = [`<span class="tk-bundle-tier__price"></span>`];
      if (compare > final) parts.push(`<s class="tk-bundle-tier__compare"></s>`);
      if (d.unit) parts.push(`<span class="tk-bundle-tier__unit"></span>`);
      prices.innerHTML = parts.join('');
      set('.tk-bundle-tier__price', this.#money(final));
      set('.tk-bundle-tier__compare', this.#money(compare));
      set('.tk-bundle-tier__unit', `${this.#money(unit)} ${d.unit ?? ''}`);
    }
  }

  /* ------------------------------------------------------------------- add to cart ---- */

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.#cart) return;
    const productForm = this.#cart.findProductForm(this, this.dataset.productId);
    if (!productForm || !productForm.contains(form)) return;
    const lines = this.pickedLines;
    if (!lines) return;
    const formId = Number(/** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value);
    // Every item is the form's own variant: Horizon adds it with the synced quantity.
    if (lines.length === 1 && lines[0]?.id === formId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const button = /** @type {HTMLButtonElement | null} */ (productForm.querySelector('[ref="addToCartButton"]'));
    this.#addPicked(lines, button);
  };

  /**
   * @param {Array<{id: number, quantity: number}>} lines
   * @param {HTMLButtonElement | null} button
   */
  async #addPicked(lines, button) {
    if (this.#busy) return;
    this.#busy = true;
    const wasDisabled = button?.disabled ?? false;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const result = await this.#cart.addLines(this, lines);
    if (button) {
      button.disabled = wasDisabled;
      button.removeAttribute('aria-busy');
    }
    this.#busy = false;
    this.#showError(result);
  }

  /** @param {{ok: boolean, message?: string}} result */
  #showError(result) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (!error) return;
    error.hidden = result.ok;
    error.textContent = result.ok ? '' : result.message ?? '';
  }

  #onClick = async (/** @type {MouseEvent} */ event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled || !this.#cart) return;

    const picked = this.pickedLines;
    if (picked) {
      await this.#addPicked(picked, button);
      return;
    }

    const productForm = this.#cart.findProductForm(this, this.dataset.productId);
    const formVariant = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="id"]'));
    const variantId = formVariant?.value || this.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const result = await this.#cart.addLines(this, [{ id: variantId, quantity: this.quantity }]);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    this.#showError(result);
  };
}

if (!customElements.get('tk-bundle')) customElements.define('tk-bundle', TkBundle);
